import { useCallback } from 'react'
import { useCameraStore } from '@modugen/ui-toolkit'
import {
  roofStoreyKey,
  useCommentFilterStore,
  useControlStore,
  useModelStore,
  usePlanImageCardStore,
  usePlanTableStore,
} from '@editorStores'
import {
  PLAN_VIEW_LABEL_ELEMENT_TYPES,
  PLAN_VIEW_ROOF_ELEMENT_TYPES,
  PLAN_VIEW_SHOW_LABEL_CROSS_SECTIONS,
  PLAN_VIEW_VISIBLE_ELEMENT_TYPES,
  PlanViewPlanType,
} from '../constants'
import { getTopRegularStorey } from '../utils'

/**
 * `storeys` are the plan's storeys (several only for the position plan). They are drawn on the
 * floorplan of their topmost regular storey; the roof alone is previewed on the top storey's
 * floorplan, roof elements only.
 */
export type ApplyPlanViewPreset = (planType: PlanViewPlanType, storeys: readonly string[]) => void

/**
 * Puts the scene into the exported plan's view: only the storeys and element types the plan draws,
 * the plan's labels (with the PDF's cross-section line for the position plan), real wall
 * footprints, the plan's coloring mode (the export's colors), the plan area frame, and the plan's
 * own comments, tables and images. Every setting is written to the existing scene stores, so the
 * regular controls stay the single source of truth — the plan view just drives them.
 */
const usePlanViewPreset = (): ApplyPlanViewPreset => {
  const setTypeVisibility = useModelStore(state => state.setTypeVisibility)
  const setTypeVisibilityByStorey = useModelStore(state => state.setTypeVisibilityByStorey)
  const setLabelVisibilityForTypes = useModelStore(state => state.setLabelVisibilityForTypes)
  const setShowRealWallThickness = useModelStore(state => state.setShowRealWallThickness)
  const setShowPlanArea = useModelStore(state => state.setShowPlanArea)
  const setShowComments = useModelStore(state => state.setShowComments)
  const setVisibleStoreys = useModelStore(state => state.setVisibleStoreys)
  const setShowLabels = useControlStore(state => state.setShowLabels)
  const setShowLabelCrossSections = useControlStore(state => state.setShowLabelCrossSections)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)
  const setColoringMode = useControlStore(state => state.setColoringMode)
  const setSelectedPlan = useCommentFilterStore(state => state.setSelectedPlan)
  const setTablesVisible = usePlanTableStore(state => state.setIsVisible)
  const setImagesVisible = usePlanImageCardStore(state => state.setIsVisible)
  const setIsOrthographic = useCameraStore(state => state.setIsOrthographic)

  return useCallback(
    (planType, storeys) => {
      const modelState = useModelStore.getState()
      // The roof has no floorplan of its own; its elements are drawn on the top storey's.
      const isRoofOnly = storeys.every(storey => storey === roofStoreyKey)
      const floorplanStorey =
        getTopRegularStorey(storeys) ?? getTopRegularStorey(modelState.availableStoreys)
      if (!storeys.length || floorplanStorey === null) return

      const visibleTypes = isRoofOnly
        ? PLAN_VIEW_ROOF_ELEMENT_TYPES
        : PLAN_VIEW_VISIBLE_ELEMENT_TYPES[planType]
      const elementTypes = Object.keys(modelState.visibilityByType) as ElementTypes[]
      elementTypes.forEach(type => setTypeVisibility(type, visibleTypes.includes(type)))
      // The export ignores the per-storey type toggles of the storey controls (which the plan view
      // doesn't show), so a type hidden for a storey elsewhere must not vanish from the preview.
      const nonRoofStoreys = [...modelState.availableStoreys].filter(s => s !== roofStoreyKey)
      const storeyTypes = Object.keys(modelState.visibilityByTypeAndStorey) as ElementTypes[]
      storeyTypes.forEach(type => setTypeVisibilityByStorey(type, nonRoofStoreys))

      setLabelVisibilityForTypes([...PLAN_VIEW_LABEL_ELEMENT_TYPES[planType]])
      setShowLabels(true)
      setShowLabelCrossSections(PLAN_VIEW_SHOW_LABEL_CROSS_SECTIONS[planType])

      setShowRealWallThickness(true)
      setShowPlanArea(true)
      // The plan coloring modes are named like the plan types.
      setColoringMode(planType)

      setSelectedPlan(planType)
      setShowComments(true)
      setTablesVisible(true)
      setImagesVisible(true)

      setVisibleStoreys([...storeys])
      setFloorplanStorey(floorplanStorey)
      setIsOrthographic(true)
    },
    [
      setColoringMode,
      setFloorplanStorey,
      setImagesVisible,
      setIsOrthographic,
      setLabelVisibilityForTypes,
      setSelectedPlan,
      setShowComments,
      setShowLabelCrossSections,
      setShowLabels,
      setShowPlanArea,
      setShowRealWallThickness,
      setTablesVisible,
      setTypeVisibility,
      setTypeVisibilityByStorey,
      setVisibleStoreys,
    ],
  )
}

export default usePlanViewPreset
